import React, { useState, useEffect, Fragment } from 'react';
import { Tab } from '@headlessui/react';
import { Dialog, Transition } from '@headlessui/react';
import { 
  BriefcaseIcon, 
  UserGroupIcon, 
  ClockIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  TagIcon,
  LinkIcon,
  UserIcon,
  CheckCircleIcon,
  XCircleIcon,
  XMarkIcon,
  PlusCircleIcon,
  MagnifyingGlassIcon,
  BoltIcon
} from '@heroicons/react/24/outline';
import { getJobContacts, db, addFastTrackContact, addRecommendedJobToUser } from '../../services/firebase';
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { toast } from 'react-hot-toast';
import ContactSelector from '../features/dashboard/jobs/ContactSelector';
import { classNames } from '../../utils/helpers';

export default function JobDetailsPanel({ job, onClose, onEdit, onDelete, isLoading = false }) {
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [recommendedUsers, setRecommendedUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showUserSearchModal, setShowUserSearchModal] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isAddingRecommendation, setIsAddingRecommendation] = useState(false);
  
  // Fast Track Contact form state
  const [showContactForm, setShowContactForm] = useState(false);
  const [isAddingContact, setIsAddingContact] = useState(false);
  const [contactError, setContactError] = useState(null);
  const [contactFirstName, setContactFirstName] = useState('');
  const [contactLastName, setContactLastName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactCompany, setContactCompany] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactSignalStrength, setContactSignalStrength] = useState(2);
  const [contactAvailableTimes, setContactAvailableTimes] = useState('');
  const [contactNotes, setContactNotes] = useState('');
  const [originalContact, setOriginalContact] = useState(null);

  useEffect(() => {
    if (job?.id) {
      fetchContacts();
      fetchRecommendedUsers();
    }
  }, [job]);
  
  const handleContactAdded = () => {
    resetContactForm();
    setShowContactForm(false);
    // Refresh contacts for the job
    fetchContacts();
    toast.success('Fast Track contact added successfully');
  };
  
  const handleAddContact = () => {
    resetContactForm();
    setShowContactForm(true);
  };
  
  const resetContactForm = () => {
    setContactFirstName('');
    setContactLastName('');
    setContactEmail('');
    setContactCompany('');
    setContactPhone('');
    setContactSignalStrength(2);
    setContactAvailableTimes('');
    setContactNotes('');
    setOriginalContact(null);
    setContactError(null);
  };
  
  const handleContactSelect = (contact) => {
    if (!contact) return;
    
    setOriginalContact(contact);
    setContactFirstName(contact.firstName || '');
    setContactLastName(contact.lastName || '');
    setContactEmail(contact.email || '');
    setContactCompany(contact.company || '');
    setContactPhone(contact.phone || '');
    setContactSignalStrength(contact.signalStrength || 2);
    setContactAvailableTimes(contact.availableTimes || '');
    setContactNotes(contact.notes || '');
  };
  
  const handleSubmitContact = async (e) => {
    e.preventDefault();
    
    if (!contactEmail) {
      setContactError('Email is required');
      return;
    }
    
    setIsAddingContact(true);
    setContactError(null);
    
    try {
      const auth = getAuth();
      const userId = auth.currentUser?.uid;
      const adminEmail = auth.currentUser?.email || 'Unknown admin';
      
      const contactData = {
        firstName: contactFirstName,
        lastName: contactLastName,
        email: contactEmail,
        company: contactCompany,
        phone: contactPhone,
        signalStrength: contactSignalStrength,
        availableTimes: contactAvailableTimes,
        notes: contactNotes,
        jobId: job.id,
        userId,
        addedBy: adminEmail,
        type: 'fast_track_connect'
      };
      
      // If we have an original contact ID, use it
      if (originalContact && originalContact.id) {
        contactData.originalContactId = originalContact.id;
      }
      
      const result = await addFastTrackContact(contactData);
      
      if (result.success) {
        handleContactAdded();
      } else {
        setContactError(result.error || 'Failed to add contact');
      }
    } catch (error) {
      console.error('Error adding contact:', error);
      setContactError(error.message || 'An unexpected error occurred');
    } finally {
      setIsAddingContact(false);
    }
  };
  
  const handleSearchUsers = async () => {
    if (!userSearchQuery.trim()) return;
    
    setIsSearching(true);
    setSearchResults([]);
    
    try {
      const usersRef = collection(db, 'user_profiles');
      const q = query(
        usersRef,
        where('email', '>=', userSearchQuery.toLowerCase()),
        where('email', '<=', userSearchQuery.toLowerCase() + '\uf8ff')
      );
      
      const querySnapshot = await getDocs(q);
      const users = [];
      
      querySnapshot.forEach((doc) => {
        const userData = doc.data();
        users.push({
          id: doc.id,
          email: userData.email,
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          displayName: userData.firstName && userData.lastName 
            ? `${userData.firstName} ${userData.lastName}` 
            : userData.email
        });
      });
      
      setSearchResults(users);
    } catch (error) {
      console.error('Error searching users:', error);
      toast.error('Failed to search users');
    } finally {
      setIsSearching(false);
    }
  };
  
  const handleSelectUser = (user) => {
    setSelectedUser(user);
  };
  
  const handleRecommendJob = async () => {
    if (!selectedUser) return;
    
    setIsAddingRecommendation(true);
    
    try {
      // Get the current admin's user ID from auth
      const auth = getAuth();
      const adminId = auth.currentUser ? auth.currentUser.uid : null;
      const adminEmail = auth.currentUser ? auth.currentUser.email : 'Unknown admin';
      
      // Get user name
      const userName = selectedUser.displayName || selectedUser.email || 'Unknown user';
      
      const result = await addRecommendedJobToUser(selectedUser.id, job.id, adminId, adminEmail, userName);
      
      if (result.success) {
        if (result.alreadyExists) {
          toast.info(`Job was already recommended to ${userName}`);
        } else {
          toast.success(`Job recommendation added for ${userName} by ${adminEmail}`);
          // Refresh the recommended users list
          fetchRecommendedUsers();
        }
        
        // Reset the search
        setUserSearchQuery('');
        setSearchResults([]);
        setSelectedUser(null);
        setShowUserSearchModal(false);
      } else {
        toast.error(`Failed to add job recommendation: ${result.error}`);
      }
    } catch (error) {
      console.error('Error adding job recommendation:', error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsAddingRecommendation(false);
    }
  };

  const fetchContacts = async () => {
    if (!job?.id) return;
    
    setLoadingContacts(true);
    try {
      const result = await getJobContacts(job.id);
      if (result.success) {
        setContacts(result.contacts || []);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoadingContacts(false);
    }
  };

  const fetchRecommendedUsers = async () => {
    if (!job?.id) return;
    
    setLoadingUsers(true);
    try {
      // Get all users who have this job in their recommendJob field
      const jobRef = doc(db, 'jobs', job.id);
      const jobDoc = await getDoc(jobRef);
      
      if (jobDoc.exists()) {
        const jobData = jobDoc.data();
        const userReactions = jobData.userReactions || {};
        
        // Extract user IDs and their reactions
        const userIds = Object.keys(userReactions);
        
        // For each user, get their profile data
        const usersData = [];
        for (const userId of userIds) {
          const userProfileRef = doc(db, 'user_profiles', userId);
          const userProfileDoc = await getDoc(userProfileRef);
          
          if (userProfileDoc.exists()) {
            const userData = userProfileDoc.data();
            usersData.push({
              id: userId,
              name: userData.firstName && userData.lastName 
                ? `${userData.firstName} ${userData.lastName}` 
                : 'Unknown User',
              email: userData.email || 'No email',
              reaction: userReactions[userId] || {},
              profileData: userData
            });
          }
        }
        
        setRecommendedUsers(usersData);
      }
    } catch (error) {
      console.error('Error fetching recommended users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Transition.Root show={!!job} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => onClose()}>
          <div className="fixed inset-0" />
          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-500 sm:duration-700"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-500 sm:duration-700"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="pointer-events-auto w-screen max-w-2xl">
                    <div className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
                      <div className="px-6 py-6 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                          <Dialog.Title className="text-xl font-semibold text-gray-900">
                            Loading Job...
                          </Dialog.Title>
                          <div className="flex items-center">
                            <button
                              type="button"
                              className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              onClick={() => onClose()}
                            >
                              <span className="sr-only">Close panel</span>
                              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="animate-pulse flex flex-col space-y-4 p-6">
                        <div className="h-6 bg-gray-200 rounded w-1/4"></div>
                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                        <div className="space-y-2">
                          <div className="h-4 bg-gray-200 rounded"></div>
                          <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                        </div>
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    );
  }

  if (!job) return null;

  return (
    <Transition.Root show={!!job} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={() => onClose()}>
        <div className="fixed inset-0" />
        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-2xl">
                  <div className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
                    {/* Header with job title and actions */}
                    <div className="px-6 py-6 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <Dialog.Title className="text-xl font-semibold text-gray-900 flex items-center">
                          <BriefcaseIcon className="h-5 w-5 mr-2 text-indigo-500" />
                          {job.title || 'Untitled Job'}
                        </Dialog.Title>
                        <div className="flex items-center space-x-3">
                          <button
                            type="button"
                            onClick={() => onEdit(job)}
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => onDelete(job)}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          >
                            Delete
                          </button>
                          <button
                            type="button"
                            className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            onClick={() => onClose()}
                          >
                            <span className="sr-only">Close panel</span>
                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                      <div className="mt-1 flex items-center text-sm text-gray-500">
                        <BuildingOfficeIcon className="h-4 w-4 mr-1 text-gray-400" />
                        {job.company || 'Unknown Company'}
                        {job.location && (
                          <>
                            <span className="mx-1">•</span>
                            <MapPinIcon className="h-4 w-4 mr-1 text-gray-400" />
                            {job.location}
                          </>
                        )}
                      </div>
      </div>

                    {/* Tab navigation */}
                    <Tab.Group>
        <Tab.List className="flex border-b border-gray-200 bg-gray-50">
          <Tab
            className={({ selected }) =>
              classNames(
                'py-3 px-4 text-sm font-medium text-gray-500 hover:text-gray-700 focus:outline-none',
                selected
                  ? 'border-b-2 border-indigo-500 text-indigo-600'
                  : 'border-b-2 border-transparent'
              )
            }
          >
            Job Details
          </Tab>
          <Tab
            className={({ selected }) =>
              classNames(
                'py-3 px-4 text-sm font-medium text-gray-500 hover:text-gray-700 focus:outline-none',
                selected
                  ? 'border-b-2 border-indigo-500 text-indigo-600'
                  : 'border-b-2 border-transparent'
              )
            }
          >
            <div className="flex items-center">
              <BoltIcon className="h-4 w-4 mr-1 text-indigo-500" />
              Fast Track Contacts ({contacts.length})
            </div>
          </Tab>
          <Tab
            className={({ selected }) =>
              classNames(
                'py-3 px-4 text-sm font-medium text-gray-500 hover:text-gray-700 focus:outline-none',
                selected
                  ? 'border-b-2 border-indigo-500 text-indigo-600'
                  : 'border-b-2 border-transparent'
              )
            }
          >
            Recommended Users ({recommendedUsers.length})
          </Tab>
        </Tab.List>

        <Tab.Panels>
          {/* Job Details Panel */}
          <Tab.Panel>
            <div className="px-4 py-5 sm:p-6">
              {/* Job Summary - Displayed at the top when available */}
              {job.summary && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-500 mb-2 flex items-center">
                    <DocumentTextIcon className="h-4 w-4 mr-1 text-gray-400" />
                    Summary
                  </h4>
                  <div className="mt-1 text-sm text-gray-900 bg-gray-50 p-4 rounded-lg whitespace-pre-wrap">
                    {job.summary}
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                {/* Basic Job Info */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-500 mb-3">Basic Information</h4>
                  <dl className="grid grid-cols-1 gap-x-4 gap-y-4">
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500 flex items-center">
                        <TagIcon className="h-4 w-4 mr-1 text-gray-400" />
                        Job Type
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">{job.type || 'Not specified'}</dd>
                    </div>
                    
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500 flex items-center">
                        <UserIcon className="h-4 w-4 mr-1 text-gray-400" />
                        Experience Level
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">{job.experienceLevel || 'Not specified'}</dd>
                    </div>
                    
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500 flex items-center">
                        <CurrencyDollarIcon className="h-4 w-4 mr-1 text-gray-400" />
                        Salary
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">{job.salary || 'Not specified'}</dd>
                    </div>
                    
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500 flex items-center">
                        <BuildingOfficeIcon className="h-4 w-4 mr-1 text-gray-400" />
                        Industry
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">{job.industry || 'Not specified'}</dd>
                    </div>
                    
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500 flex items-center">
                        <TagIcon className="h-4 w-4 mr-1 text-gray-400" />
                        Role Type
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">{job.roleType || 'Not specified'}</dd>
                    </div>
                    
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500 flex items-center">
                        <TagIcon className="h-4 w-4 mr-1 text-gray-400" />
                        Workplace
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">{job.workplace || 'Not specified'}</dd>
                    </div>
                    
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500 flex items-center">
                        <TagIcon className="h-4 w-4 mr-1 text-gray-400" />
                        Work Environment
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">{job.workEnvironment || 'Not specified'}</dd>
                    </div>
                    
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500 flex items-center">
                        <TagIcon className="h-4 w-4 mr-1 text-gray-400" />
                        Status
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {job.status === 'active' && (
                          <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                            Active
                          </span>
                        )}
                        {job.status === 'inactive' && (
                          <span className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-600/20">
                            Inactive
                          </span>
                        )}
                        {job.status === 'pending' && (
                          <span className="inline-flex items-center rounded-md bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-700 ring-1 ring-inset ring-yellow-600/20">
                            Pending
                          </span>
                        )}
                        {!job.status && 'Unknown'}
                      </dd>
                    </div>
                  </dl>
                </div>
                
                {/* Dates and Links */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-500 mb-3">Dates & Links</h4>
                  <dl className="grid grid-cols-1 gap-x-4 gap-y-4">
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500 flex items-center">
                        <ClockIcon className="h-4 w-4 mr-1 text-gray-400" />
                        Posted Date
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">{job.postedDate || 'Not specified'}</dd>
                    </div>
                    
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500 flex items-center">
                        <ClockIcon className="h-4 w-4 mr-1 text-gray-400" />
                        Application Deadline
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">{job.applicationDeadline || 'Not specified'}</dd>
                    </div>
                    
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500 flex items-center">
                        <ClockIcon className="h-4 w-4 mr-1 text-gray-400" />
                        Created At
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {job.createdAt ? new Date(job.createdAt.seconds * 1000).toLocaleDateString() : 'Unknown'}
                      </dd>
                    </div>
                    
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500 flex items-center">
                        <ClockIcon className="h-4 w-4 mr-1 text-gray-400" />
                        Updated At
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {job.updatedAt ? new Date(job.updatedAt.seconds * 1000).toLocaleDateString() : 'Unknown'}
                      </dd>
                    </div>
                    
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500 flex items-center">
                        <LinkIcon className="h-4 w-4 mr-1 text-gray-400" />
                        Application URL
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {job.applicationUrl ? (
                          <a href={job.applicationUrl} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-900">
                            {job.applicationUrl}
                          </a>
                        ) : (
                          'Not specified'
                        )}
                      </dd>
                    </div>
                    
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500 flex items-center">
                        <TagIcon className="h-4 w-4 mr-1 text-gray-400" />
                        Source
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {job.source === 'admin' && (
                          <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20">
                            Admin
                          </span>
                        )}
                        {job.source === 'user' && (
                          <span className="inline-flex items-center rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-600/20">
                            User
                          </span>
                        )}
                        {job.source === 'employer' && (
                          <span className="inline-flex items-center rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700 ring-1 ring-inset ring-purple-600/20">
                            Employer
                          </span>
                        )}
                        {!job.source && 'Unknown'}
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
              
              {/* Job Description and Details */}
                <div className="mt-6 space-y-6">
                
                {job.description && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2 flex items-center">
                      <DocumentTextIcon className="h-4 w-4 mr-1 text-gray-400" />
                      Description
                    </h4>
                    <div className="mt-1 text-sm text-gray-900 bg-gray-50 p-4 rounded-lg whitespace-pre-wrap">
                      {job.description}
                    </div>
                  </div>
                )}
                
                {job.responsibilities && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2 flex items-center">
                      <DocumentTextIcon className="h-4 w-4 mr-1 text-gray-400" />
                      Responsibilities
                    </h4>
                    <div className="mt-1 text-sm text-gray-900 bg-gray-50 p-4 rounded-lg whitespace-pre-wrap">
                      {job.responsibilities}
                    </div>
                  </div>
                )}
                
                {job.requirements && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2 flex items-center">
                      <DocumentTextIcon className="h-4 w-4 mr-1 text-gray-400" />
                      Requirements
                    </h4>
                    <div className="mt-1 text-sm text-gray-900 bg-gray-50 p-4 rounded-lg whitespace-pre-wrap">
                      {job.requirements}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Tab.Panel>

          {/* Fast Track Contacts Panel */}
          <Tab.Panel>
            <div className="px-4 py-5 sm:p-6">
              <div className="mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <BoltIcon className="h-5 w-5 mr-2 text-indigo-600" />
                  Fast Track Contacts
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  These contacts can help fast-track applications for this job position.
                </p>
              </div>
                  {!showContactForm ? (
                    <div>
                      <div className="mb-4 flex justify-end">
                        <button
                          type="button"
                          onClick={handleAddContact}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          <PlusCircleIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                          Add Fast Track Contact
                        </button>
                      </div>
                      
                      {loadingContacts ? (
                        <div className="animate-pulse space-y-4">
                          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                          <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                        </div>
                      ) : contacts.length === 0 ? (
                        <div className="text-center py-6 bg-gray-50 rounded-lg">
                          <BoltIcon className="mx-auto h-12 w-12 text-gray-400" />
                          <h3 className="mt-2 text-sm font-medium text-gray-900">No fast track contacts</h3>
                          <p className="mt-1 text-sm text-gray-500">Add contacts who can help candidates fast-track their applications for this job.</p>
                          <div className="mt-6">
                            <button
                              type="button"
                              onClick={handleAddContact}
                              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                              <PlusCircleIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                              Add First Contact
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="overflow-hidden shadow ring-1 ring-black/5 sm:rounded-lg">
                          <table className="min-w-full divide-y divide-gray-300">
                            <thead className="bg-gray-50">
                              <tr>
                                <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                                  Contact
                                </th>
                                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                  Company
                                </th>
                                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                  Response Likelihood
                                </th>
                                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                  Available Times
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                              {contacts.map((contact) => (
                                <tr key={contact.id}>
                                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                                    <div>
                                      {contact.firstName} {contact.lastName}
                                    </div>
                                    <div className="text-gray-500">{contact.email}</div>
                                    {contact.phone && <div className="text-gray-500">{contact.phone}</div>}
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                    {contact.company || 'N/A'}
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                    <div className="flex items-center">
                                      {[1, 2, 3].map((level) => (
                                        <div
                                          key={level}
                                          className={`h-2 w-2 rounded-full mx-0.5 ${level <= (contact.signalStrength || 0) ? 'bg-green-500' : 'bg-gray-200'}`}
                                        />
                                      ))}
                                      <span className="ml-2">
                                        {contact.signalStrength === 1 && 'Low'}
                                        {contact.signalStrength === 2 && 'Medium'}
                                        {contact.signalStrength === 3 && 'High'}
                                        {!contact.signalStrength && 'Unknown'}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                    {contact.availableTimes || 'Not specified'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-medium text-gray-900">Add Fast Track Contact</h3>
                        <button
                          type="button"
                          className="text-gray-400 hover:text-gray-500"
                          onClick={() => setShowContactForm(false)}
                        >
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                      </div>
                      
                      <ContactSelector 
                        onSelectContact={handleContactSelect} 
                        jobId={job.id} 
                        userId={getAuth().currentUser?.uid} 
                      />
                      
                      <form onSubmit={handleSubmitContact} className="space-y-4 mt-4">
                        {contactError && (
                          <div className="rounded-md bg-red-50 p-4 mb-4">
                            <div className="flex">
                              <div className="flex-shrink-0">
                                <XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
                              </div>
                              <div className="ml-3">
                                <h3 className="text-sm font-medium text-red-800">{contactError}</h3>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                          <div>
                            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                              First name
                            </label>
                            <div className="mt-1">
                              <input
                                type="text"
                                name="firstName"
                                id="firstName"
                                value={contactFirstName}
                                onChange={(e) => setContactFirstName(e.target.value)}
                                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                              />
                            </div>
                          </div>

                          <div>
                            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                              Last name
                            </label>
                            <div className="mt-1">
                              <input
                                type="text"
                                name="lastName"
                                id="lastName"
                                value={contactLastName}
                                onChange={(e) => setContactLastName(e.target.value)}
                                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                              />
                            </div>
                          </div>
                          
                          <div className="sm:col-span-2">
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                              Email <span className="text-red-500">*</span>
                            </label>
                            <div className="mt-1">
                              <input
                                type="email"
                                name="email"
                                id="email"
                                value={contactEmail}
                                onChange={(e) => setContactEmail(e.target.value)}
                                required
                                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                              />
                            </div>
                          </div>
                          
                          <div>
                            <label htmlFor="company" className="block text-sm font-medium text-gray-700">
                              Company
                            </label>
                            <div className="mt-1">
                              <input
                                type="text"
                                name="company"
                                id="company"
                                value={contactCompany}
                                onChange={(e) => setContactCompany(e.target.value)}
                                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                              />
                            </div>
                          </div>
                          
                          <div>
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                              Phone
                            </label>
                            <div className="mt-1">
                              <input
                                type="text"
                                name="phone"
                                id="phone"
                                value={contactPhone}
                                onChange={(e) => setContactPhone(e.target.value)}
                                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                              />
                            </div>
                          </div>
                          
                          <div className="sm:col-span-2">
                            <label htmlFor="signalStrength" className="block text-sm font-medium text-gray-700">
                              Response Likelihood
                            </label>
                            <div className="mt-1">
                              <select
                                id="signalStrength"
                                name="signalStrength"
                                value={contactSignalStrength}
                                onChange={(e) => setContactSignalStrength(Number(e.target.value))}
                                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                              >
                                <option value={1}>Low - May not respond quickly</option>
                                <option value={2}>Medium - Typically responds within a week</option>
                                <option value={3}>High - Usually responds within 48 hours</option>
                              </select>
                            </div>
                          </div>
                          
                          <div className="sm:col-span-2">
                            <label htmlFor="availableTimes" className="block text-sm font-medium text-gray-700">
                              Available Times
                            </label>
                            <div className="mt-1">
                              <input
                                type="text"
                                name="availableTimes"
                                id="availableTimes"
                                value={contactAvailableTimes}
                                onChange={(e) => setContactAvailableTimes(e.target.value)}
                                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                placeholder="e.g., Weekdays 9-5 EST"
                              />
                            </div>
                          </div>
                          
                          <div className="sm:col-span-2">
                            <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                              Notes
                            </label>
                            <div className="mt-1">
                              <textarea
                                id="notes"
                                name="notes"
                                rows={3}
                                value={contactNotes}
                                onChange={(e) => setContactNotes(e.target.value)}
                                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                placeholder="Any additional information about this contact"
                              />
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex justify-end space-x-3 pt-4">
                          <button
                            type="button"
                            className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            onClick={() => setShowContactForm(false)}
                            disabled={isAddingContact}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 flex items-center"
                            disabled={isAddingContact}
                          >
                            {isAddingContact && (
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            )}
                            {isAddingContact ? 'Saving...' : 'Save Contact'}
                          </button>
                        </div>
                      </form>
                    </div>
                  )}
            </div>
          </Tab.Panel>

          {/* Recommended Users Panel */}
          <Tab.Panel>
            <div className="px-4 py-5 sm:p-6">
              <div className="mb-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowUserSearchModal(true)}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <PlusCircleIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                  Recommend to User
                </button>
              </div>
              
              {loadingUsers ? (
                <div className="animate-pulse space-y-4">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                </div>
              ) : recommendedUsers.length === 0 ? (
                <div className="text-center py-6">
                  <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No recommended users</h3>
                  <p className="mt-1 text-sm text-gray-500">This job hasn't been recommended to any users yet.</p>
                </div>
              ) : (
                <div className="overflow-hidden shadow ring-1 ring-black/5 sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                          User
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Reaction
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Last Activity
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Recommended On
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Recommended By
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {recommendedUsers.map((user) => (
                        <tr key={user.id}>
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                            <div>{user.name}</div>
                            <div className="text-gray-500">{user.email}</div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {user.reaction.reaction === 'like' && (
                              <span className="inline-flex items-center text-green-600">
                                <CheckCircleIcon className="h-5 w-5 mr-1" />
                                Liked
                              </span>
                            )}
                            {user.reaction.reaction === 'dislike' && (
                              <span className="inline-flex items-center text-red-600">
                                <XCircleIcon className="h-5 w-5 mr-1" />
                                Disliked
                              </span>
                            )}
                            {!user.reaction.reaction && 'No reaction'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {user.reaction.lastActivity ? (
                              new Date(user.reaction.lastActivity.seconds * 1000).toLocaleDateString()
                            ) : (
                              'No activity'
                            )}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {user.reaction.recommendedAt ? (
                              new Date(user.reaction.recommendedAt.seconds * 1000).toLocaleDateString()
                            ) : (
                              'Unknown'
                            )}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {user.reaction.recommendedBy || 'Unknown'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </Tab.Panel>
        </Tab.Panels>
                    </Tab.Group>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
      
      {/* Fast Track Contact functionality is now integrated directly in the panel */}
      
      {/* User Search Modal */}
      <Transition.Root show={showUserSearchModal} as={Fragment}>
        <Dialog as="div" className="relative z-20" onClose={() => setShowUserSearchModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div>
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-indigo-100">
                      <UserGroupIcon className="h-6 w-6 text-indigo-600" aria-hidden="true" />
                    </div>
                    <div className="mt-3 text-center sm:mt-5">
                      <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                        Recommend Job to User
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Search for a user by email to recommend this job to them.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-5">
                    <div className="flex rounded-md shadow-sm">
                      <div className="relative flex flex-grow items-stretch focus-within:z-10">
                        <input
                          type="email"
                          name="email"
                          id="email"
                          className="block w-full rounded-none rounded-l-md border-0 py-1.5 pl-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                          placeholder="Search by email address"
                          value={userSearchQuery}
                          onChange={(e) => setUserSearchQuery(e.target.value)}
                        />
                      </div>
                      <button
                        type="button"
                        className="relative -ml-px inline-flex items-center gap-x-1.5 rounded-r-md px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                        onClick={handleSearchUsers}
                        disabled={isSearching}
                      >
                        {isSearching ? (
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        ) : (
                          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                        )}
                        Search
                      </button>
                    </div>
                  </div>
                  
                  <div className="mt-4 max-h-60 overflow-y-auto">
                    {searchResults.length === 0 && userSearchQuery && !isSearching ? (
                      <p className="text-sm text-gray-500 text-center py-4">No users found matching your search.</p>
                    ) : (
                      <ul className="divide-y divide-gray-200">
                        {searchResults.map((user) => (
                          <li key={user.id} className="py-3 flex items-center">
                            <input
                              type="radio"
                              name="selected-user"
                              checked={selectedUser?.id === user.id}
                              onChange={() => handleSelectUser(user)}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            />
                            <div className="ml-3">
                              <p className="text-sm font-medium text-gray-900">{user.firstName} {user.lastName}</p>
                              <p className="text-sm text-gray-500">{user.email}</p>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  
                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:col-start-2"
                      onClick={handleRecommendJob}
                      disabled={!selectedUser || isAddingRecommendation}
                    >
                      {isAddingRecommendation ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Recommending...
                        </>
                      ) : 'Recommend Job'}
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                      onClick={() => setShowUserSearchModal(false)}
                      disabled={isAddingRecommendation}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </Transition.Root>
  );
}
